"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { useLocalizedInput } from "@/hooks/useLocalizedInput";
import {
  getInputNormalizer,
  NUMBER_INPUT_PATTERN,
} from "@/lib/forms/localized-input";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------

const customInputVariant = cva(
  [
    "px-3 py-2 flex leading-4 w-full",
    "text-foreground rounded-xs",
    "border border-input/65 dark:border-input/35",
    "ring-offset-background bg-transparent",
    "file:border-0 file:bg-transparent file:text-sm file:font-medium",
    "placeholder:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 focus-visible:border-0",
    "disabled:cursor-not-allowed disabled:opacity-35",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-10 text-sm",
        md: "h-14 text-base py-4",
        lg: "h-16 text-lg",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

interface FloatingInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof customInputVariant> {
  size?: "sm" | "md" | "lg" | undefined;
  browserOwned?: boolean;
}

const CustomInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  (
    {
      className,
      type = "text",
      size,
      dir,
      inputMode,
      value,
      defaultValue,
      onChange,
      onBlur,
      browserOwned,
      onInput,
      onCompositionStart,
      onCompositionEnd,
      ...props
    },
    ref,
  ) => {
    const isNumberInput = type === "number";
    const resolvedDir = isNumberInput ? "ltr" : dir;
    const normalize = getInputNormalizer(type, inputMode);
    const { valueProps, inputEvents } = useLocalizedInput<HTMLInputElement>({
      normalize,
      browserOwned,
      ref,
      value,
      defaultValue,
      numberConstraints: isNumberInput ? props : undefined,
      nativeInputFallback: [
        "text",
        "number",
        "tel",
        "search",
        "email",
        "url",
        "password",
      ].includes(type),
      onChange,
      onBlur,
      onInput,
      onCompositionStart,
      onCompositionEnd,
    });

    return (
      <input
        type={isNumberInput ? "text" : type}
        inputMode={inputMode ?? (isNumberInput ? "decimal" : undefined)}
        {...valueProps}
        dir={resolvedDir}
        className={cn(
          customInputVariant({ size, className }),
          isNumberInput &&
            "text-left [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none",
        )}
        {...props}
        pattern={
          props.pattern ?? (isNumberInput ? NUMBER_INPUT_PATTERN : undefined)
        }
        {...inputEvents}
      />
    );
  },
);
CustomInput.displayName = "CustomInput";

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ className, ...props }, ref) => {
    return (
      <CustomInput
        className={cn(
          "peer bg-transparent border-none placeholder:text-transparent focus:placeholder:text-muted transition-all duration-300",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
FloatingInput.displayName = "FloatingInput";

export { FloatingInput, CustomInput, type FloatingInputProps };
